import React, { useState, useEffect } from 'react';
import "./Navbar.css"
import { AiOutlineMenu, AiOutlineClose } from "react-icons/ai";
import uaLogo2 from "../../assets/uaLogo2.png"
import uaLogo from "../../assets/uaaLogo.png"
import useLocalStorage from 'use-local-storage';
import { Link, useLocation } from 'react-router-dom';

// REACT ICONS
import { IoCloudyNightOutline } from "react-icons/io5";
import { MdLightMode } from "react-icons/md";

function Navbar() {
    const [toggle, setToggle] = useState(false);
    const location = useLocation();

    const handleToggle = () => setToggle(!toggle);

    // DAYNIGHT THEME SESSION
    const [theme, setTheme] = useLocalStorage('theme' ? 'dark' : 'light')

    const themeSwitch = () => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme)
    }

    // Function to check if a link is active
    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const mobileNav = document.querySelector('.mobile-nav');
            const toggleButton = document.querySelector('.menu_btn');
            if (mobileNav && !mobileNav.contains(event.target) && !toggleButton.contains(event.target)) {
                setToggle(false); // Hide mobile nav
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className='sticky top-0 z-50 transition-all duration-300'>
            {/* Glassmorphism Background */}
            <div className={`absolute inset-0 backdrop-blur-md transition-colors duration-300 ${toggle ? "opacity-100" : "opacity-90"} ${theme === 'dark' ? 'bg-[#080810]/80 border-b border-gray-800' : 'bg-[#f7f6f6]/80 border-b border-gray-200'}`}></div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">

                    {/* Logo Section */}
                    <div className="flex-shrink-0 cursor-pointer z-50">
                        <Link to="/" className="flex items-center gap-2">
                            {
                                theme === "dark" ? <img src={uaLogo} className="w-12 h-12 object-contain transition-transform hover:scale-105" alt='my_logo' /> : <img src={uaLogo2} className="w-12 h-12 object-contain transition-transform hover:scale-110" alt='my_logo' />
                            }
                        </Link>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:block">
                        <div className="ml-10 flex items-baseline space-x-8 font-techFont">
                            {['/', '/about', '/awards', '/projects', '/experience', '/contact'].map((path) => {
                                const label = path === '/' ? 'Home' : path.substring(1).charAt(0).toUpperCase() + path.substring(1).slice(1);
                                const isCert = path === '/awards';
                                const displayLabel = isCert ? 'Certificates' : label;

                                return (
                                    <Link key={path} to={path} className="group relative">
                                        <span className={`px-3 py-2 text-lg font-medium transition-colors duration-300 ${isActive(path)
                                            ? (theme === 'dark' ? 'text-white' : 'text-gray-900')
                                            : (theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900')
                                            }`}>
                                            {displayLabel}
                                        </span>
                                        <span className={`absolute bottom-0 left-0 w-full h-0.5 transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100 ${theme === 'dark' ? 'bg-white' : 'bg-gray-900'
                                            } ${isActive(path) ? 'scale-x-100' : ''}`} />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>

                    {/* Theme Toggle & Mobile Menu Button */}
                    <div className="flex items-center gap-4 z-50">
                        <button
                            onClick={themeSwitch}
                            className={`p-2 rounded-full transition-all duration-300 hover:scale-110 ${theme === 'dark' ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                }`}
                            aria-label="Toggle Theme"
                        >
                            {theme === "dark" ? <MdLightMode size={20} /> : <IoCloudyNightOutline size={20} />}
                        </button>

                        <div className="md:hidden">
                            <button
                                onClick={handleToggle}
                                className={`p-2 rounded-md transition-colors ${theme === 'dark' ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                                    }`}
                            >
                                {!toggle ? <AiOutlineMenu size={24} /> : <AiOutlineClose size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            <div className={`
                md:hidden fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out
                ${toggle ? 'translate-x-0' : '-translate-x-full'}
                ${theme === 'dark' ? 'bg-[#080810]/95 backdrop-blur-xl' : 'bg-[#f7f6f6]/95 backdrop-blur-xl'}
            `}>
                <div className="flex flex-col items-center justify-start pt-28 h-full space-y-6 font-techFont">
                    {['/', '/about', '/awards', '/projects', '/experience', '/contact'].map((path, index) => {
                        const label = path === '/' ? 'Home' : path.substring(1).charAt(0).toUpperCase() + path.substring(1).slice(1);
                        const isCert = path === '/awards';
                        const displayLabel = isCert ? 'Certificates' : label;

                        return (
                            <Link
                                key={path}
                                to={path}
                                onClick={() => setToggle(false)}
                                style={{ animationDelay: `${index * 100}ms` }}
                                className={`text-lg font-medium transition-all duration-300 w-[85%] text-center py-3 rounded-2xl ${toggle ? 'animate-slide-up' : 'opacity-0'} ${isActive(path)
                                    ? (theme === 'dark' ? 'bg-white/10 text-white border border-white/5 shadow-lg backdrop-blur-sm' : 'bg-black/5 text-gray-900 border border-black/5 shadow-inner')
                                    : (theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-black/5')
                                    }`}
                            >
                                {displayLabel}
                            </Link>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default Navbar